'use client';

import React, { useRef, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import {
  runScrollTriggerSetup,
  schedulePerformanceLabScrollTriggerRefresh,
} from 'utils/performanceLabScrollTrigger';
import {
  applyRecoveryThemeProgress,
  RECOVERY_CAROUSEL_THEME_TRIGGER_SELECTOR,
  setRecoveryThemeLight,
} from './recoveryThemeVars';
import styles from './RecoveryThemeTransition.module.css';

gsap.registerPlugin(ScrollTrigger);

const SCROLL_SCRUB = 0.6;
/** Carousel scroll-off before the black → light transition runs (fraction of carousel height). */
const TRANSITION_START_OFFSCREEN = 0.15;
const TRANSITION_END_OFFSCREEN = 0.6;

function attachCarouselImageLoadRefresh(
  carousel: HTMLElement,
): () => void {
  const images = Array.from(carousel.querySelectorAll('img'));
  if (!images.length) return () => {};

  let pending = images.length;
  let cancelled = false;
  let cancelScheduledRefresh: (() => void) | null = null;

  const handleImageSettled = () => {
    if (cancelled) return;
    pending -= 1;
    if (pending > 0) return;

    cancelScheduledRefresh?.();
    cancelScheduledRefresh = schedulePerformanceLabScrollTriggerRefresh();
  };

  images.forEach((image) => {
    if (image.complete) {
      handleImageSettled();
      return;
    }

    image.addEventListener('load', handleImageSettled, { once: true });
    image.addEventListener('error', handleImageSettled, { once: true });
  });

  return () => {
    cancelled = true;
    cancelScheduledRefresh?.();
    images.forEach((image) => {
      image.removeEventListener('load', handleImageSettled);
      image.removeEventListener('error', handleImageSettled);
    });
  };
}

type RecoveryThemeTransitionProps = {
  children: ReactNode;
};

export const RecoveryThemeTransition: React.FC<RecoveryThemeTransitionProps> = ({
  children,
}) => {
  const zoneRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const zone = zoneRef.current;
    if (!zone) return;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (prefersReducedMotion) {
      setRecoveryThemeLight(zone);
      return;
    }

    let cleanupImageRefresh: (() => void) | null = null;

    const cleanupSetup = runScrollTriggerSetup({
      debugLabel: 'RecoveryThemeTransition carousel anchor',
      isReady: () => {
        const currentZone = zoneRef.current;
        if (!currentZone) return false;
        return Boolean(
          currentZone.querySelector<HTMLElement>(
            RECOVERY_CAROUSEL_THEME_TRIGGER_SELECTOR,
          ),
        );
      },
      setup: () => {
        const currentZone = zoneRef.current;
        const carousel = currentZone?.querySelector<HTMLElement>(
          RECOVERY_CAROUSEL_THEME_TRIGGER_SELECTOR,
        );
        if (!currentZone || !carousel) return;

        applyRecoveryThemeProgress(currentZone, 0);

        const start = `${TRANSITION_START_OFFSCREEN * 100}% top`;
        const end = `${TRANSITION_END_OFFSCREEN * 100}% top`;

        const trigger = ScrollTrigger.create({
          trigger: carousel,
          start,
          end,
          scrub: SCROLL_SCRUB,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            applyRecoveryThemeProgress(currentZone, self.progress);
          },
        });

        cleanupImageRefresh = attachCarouselImageLoadRefresh(carousel);

        return {
          cleanup: () => {
            cleanupImageRefresh?.();
            cleanupImageRefresh = null;
            trigger.kill();
          },
        };
      },
    });

    return () => {
      cleanupImageRefresh?.();
      cleanupSetup();
    };
  }, []);

  return (
    <div className={styles.themeZone} ref={zoneRef}>
      {children}
    </div>
  );
};
